/* eslint-disable @next/next/no-img-element */
import { formatCurrency } from '@/helpers/format';
import usePrefix from '@/hooks/usePrefix';
import { useMobxStores } from '@/stores';
import {
  Popover,
  Rate,
  Tabs,
} from 'antd';
import Image from 'next/image';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import ButtonStatus from './ButtonStatus2';
import {
  CheckOutlined,
} from './Icons';
import SidePopover from './FacilityHighlights/SidePopover';
import MiniHorizontal from './FacilityHighlights/MiniHorizontal';
import { isShowHighlights } from '@/helpers/highlights';
import { CDN1, CDN2 } from '@/helpers/api';
import { mapRateSearchName, mapReviewStars } from '@/utils/tools';
import { getParkingAtPath, getUrlCodeOrSlug } from '@/utils/newparkingatroutes';
import NeedMoreData from './NeedMoreData';
import { IAirport } from '@/types';
import { isServer } from '@/utils/isServer';
import { setStorageCheckinCheckout } from '@/hooks/useLocalstorage';
import { useTranslation } from 'react-i18next';
import VerticalEX from './FacilityHighlights/VerticalEX';
import SearchImage from './SearchImage';

function checkShowFull(facility_url_code: string) {
  return !facility_url_code?.toLocaleLowerCase().startsWith('arv');
}

const Card = ({
  configInfo,
  item,
  index,
  cardsRef,
  checkin,
  checkout,
  hasHighlight,
  deviceType,
  isCity,
}) => {
  const prefix = usePrefix();
  const router = useRouter();
  const { searchStore } = useMobxStores();
  const [showWhatParkBtn, setShowWhatParkBtn] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>('description');
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);

  const { TabPane } = Tabs;

  const openMoreInformation = async (e): Promise<void> => {
    e.preventDefault();
    setShowWhatParkBtn(!showWhatParkBtn);
    setActiveKey('description');
    return;
  };

  const openViewHighlights = async (e): Promise<void> => {
    e.preventDefault();
    setShowWhatParkBtn(!showWhatParkBtn);
    setActiveKey('highlights');
    return;
  };

  function preventClick(e) {
    if (!e) e = window.event;
    e.stopPropagation();
    return;
  }

  useEffect(() => {
    sessionStorage.removeItem('checkin');
    sessionStorage.removeItem('checkout');
  }, []);

  const UnbrandedImages = [
    CDN2() + '/staticmyapp/cards-default-image.webp',
    CDN2() + '/staticmyapp/pvd-airport.webp',
    CDN2() + '/staticmyapp/default-image-3.webp',
  ];

  let lastUnbrandedIndex = null;

  const getUnbrandedImage = () => {
    let randomIndex = index % UnbrandedImages.length;
    if (randomIndex === lastUnbrandedIndex) {
      randomIndex = (randomIndex + 1) % UnbrandedImages.length;
    }
    lastUnbrandedIndex = randomIndex;
    return UnbrandedImages[randomIndex];
  };

  let imageUrl = item.facility_is_image_uploaded
    ? `${CDN1}/upload/${configInfo.environment}/${item.facility_id}`
    : getUnbrandedImage();

  const arriveLogo = item.highlights.find((x) => x.type === 'arrive_logo_url');
  if (arriveLogo) {
    imageUrl = arriveLogo.description;
  }

  return item.date_sold_out > 0 ? (
    <>
      <div
        key={item.facility_id + index}
        ref={(el) => (cardsRef.current[index] = el)}
        className={`relative mx-auto bg-white rounded-[0.675rem] border shadow-lg overflow-hidden  transition-all duration-300 ${item.selected ? 'shadow-2xl' : ''} opacity-60`}
      >
        <div className="flex flex-row w-full overflow-hidden">
          <div className="flex-shrink-0 content-center">
            <div className="relative w-32 xl:w-64 h-full overflow-hidden rounded-tl-md rounded-bl-md">
              <SearchImage
                src={imageUrl}
                native={!!arriveLogo}
                placeholder="blur"
                fill
                style={{ objectFit: 'contain' }}
                blurDataURL={getUnbrandedImage()}
                errorImage={getUnbrandedImage()}
                alt={`Airport: ${item.facility_lot} Background`}
                key={item.facility_id}
                quality={100}
              />
            </div>
          </div>

          <div className="flex flex-col flex-1 min-w-0 overflow-hidden p-2 xl:p-4">
            <div className="flex flex-col justify-center min-w-0 overflow-hidden">
              <h3 className="text-xl font-bold truncate">
                {item.facility_lot}
              </h3>
              {checkShowFull(item.facility_url_code) && (
                <div className="flex flex-row min-w-0 overflow-hidden items-center">
                  <p
                    className={`text-white font-semibold rounded-md p-1.5 text-xs text-center bg-green-600`}
                  >
                    {item.facility_review_avg.toFixed(1)}
                  </p>
                  <div className="flex flex-col ml-2 leading-tight truncate">
                    <p className="text-base font-bold truncate">
                      <span>
                        {item.facility_review_avg >= 4.5
                          ? 'Excellent!'
                          : item.facility_review_avg >= 4.0
                            ? 'Very Good!'
                            : 'Good!'}
                      </span>
                    </p>
                    <p className="text-xs truncate">
                      {item.num_review}{' '}
                      {item.num_review === 1 ? 'review' : 'reviews'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col xl:flex-row justify-between items-start gap-4 min-w-0 overflow-hidden mt-6">
              <ButtonStatus
                status={searchStore.facilityStatus(item)}
                facility_url_code={item.facility_url_code}
                minDays={item.facility_min_days}
                maxDays={item.facility_max_days}
                configInfo={configInfo}
                item={item}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <Link
      href={`${prefix}/${getParkingAtPath(item)}/${getUrlCodeOrSlug(item)}`}
      key={item.facility_id + index}
      passHref
      legacyBehavior
    >
      <div
        key={item.facility_id + index}
        ref={(el) => (cardsRef.current[index] = el)}
        className="border rounded-md overflow-hidden shadow-md transition-all duration-300 bg-white relative cursor-pointer"
      >
        <div className="flex flex-row w-full overflow-hidden">
          <div className="flex-shrink-0 content-center">
            <div className="relative w-32 xl:w-64 h-full overflow-hidden rounded-tl-md rounded-bl-md">
              <SearchImage
                src={imageUrl}
                native={!!arriveLogo}
                placeholder="blur"
                fill
                style={{ objectFit: 'contain' }}
                blurDataURL={getUnbrandedImage()}
                errorImage={getUnbrandedImage()}
                alt={`Airport: ${item.facility_lot} Background`}
                key={item.facility_id}
                quality={100}
              />
            </div>
          </div>

          <div className="flex flex-col flex-1 min-w-0 overflow-hidden p-2 xl:p-4">
            <div className="flex flex-col justify-center min-w-0 overflow-hidden">
              <h3 className="text-xl font-bold truncate">
                {item.facility_lot}
              </h3>
              {!isCity && (
                <p className="text-sm truncate">
                  {item.facility_airport_distance} mi from {item.airport_name} ({item?.airport_initials})
                </p>
              )}
              {isShowHighlights(item.highlights) && (
                <div className="mt-3">
                  <Popover
                    content={
                      <SidePopover
                        facility={item}
                        highlights={item.highlights}
                        size="small"
                      />
                    }
                    trigger={deviceType === 'desktop' ? 'hover' : 'click'}
                    overlayClassName={`${deviceType !== 'desktop' ? 'search-list-popover' : 'search-list-popover-desktop'}`}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <VerticalEX
                        facility={item}
                        highlights={item.highlights}
                        size="small"
                        className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 items-start justify-items-start"
                        isCity={isCity}
                      />
                    </div>
                  </Popover >
                </div >
              )}
            </div >

            <div className="flex flex-col xl:flex-row justify-between items-start gap-4 min-w-0 overflow-hidden mt-6">
              <div className="flex flex-col justify-between h-full">
                {!item.facility_disable_lowest_price ? (
                  <p
                    className={`rounded-md text-sm flex items-center justify-center text-green-800 `}
                  >
                    Lowest Price Guarantee*
                  </p>
                ) : <div></div>}
                {checkShowFull(item.facility_url_code) && (
                  <div className="flex flex-row min-w-0 overflow-hidden items-center">
                    <p
                      className={`text-white font-semibold rounded-md p-1.5 text-xs text-center bg-green-600`}
                    >
                      {item.facility_review_avg.toFixed(1)}
                    </p>
                    <div className="flex flex-col ml-2 leading-tight truncate">
                      <p className="text-base font-bold truncate">
                        <span>
                          {item.facility_review_avg >= 4.5
                            ? 'Excellent!'
                            : item.facility_review_avg >= 4.0
                              ? 'Very Good!'
                              : 'Good!'}
                        </span>
                      </p>
                      <p className="text-xs truncate">
                        {item.num_review}{' '}
                        {item.num_review === 1 ? 'review' : 'reviews'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col justify-center items-end text-right flex-shrink-0 self-end">
                {item.facilityParkingType == 3 || item.facilityParkingType == 4 ? (
                  <div>
                    <p className="text-base">
                      {formatCurrency({
                        value: item.facility_solid_selling_price
                          ? item.facility_solid_selling_price
                          : item.facility_afee,
                        locale: 'en-US',
                        currency: 'USD',
                      })}{' '}
                      <small className="text-sm text-onair-gray">
                        {item.facilityParkingType == 3
                          ? 'Sleep and Park'
                          : 'Monthly Park'}
                      </small>
                    </p>
                    <p className="text-xl font-bold lowercase">
                      {formatCurrency({
                        value: item.facility_total_price,
                        locale: 'en-US',
                        currency: 'USD',
                      })}{' '}
                      <small className="text-base text-onair-gray font-bold">
                        total
                      </small>
                    </p>
                    <small className="text-xs text-green-600">
                      <CheckOutlined className="mr-2" />
                      Total includes taxes and fees
                    </small>
                  </div>
                ) : (
                  <div>
                    <p className="text-base">
                      {formatCurrency({
                        value: item.facility_selling_price,
                        locale: 'en-US',
                        currency: 'USD',
                      })}{' '}
                      <small className="text-sm text-onair-gray lowercase">
                        {mapRateSearchName(item)}
                      </small>
                    </p>
                    <p className="text-xl font-bold">
                      {formatCurrency({
                        value: item.facility_total_price,
                        locale: 'en-US',
                        currency: 'USD',
                      })}{' '}
                      <small className="text-base text-onair-gray font-bold">
                        total
                      </small>
                    </p>
                    <small className="text-xs text-green-600">
                      <CheckOutlined className="mr-2" />
                      Total includes taxes and fees
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div >
        </div >
      </div >
    </Link >
  );
};

function SearchList({
  configInfo,
  checkin,
  checkout,
  deviceType,
  searchAlternate,
  currentAirport,
  isNewLanding,
  isCity,
  testScope,
}: {
  configInfo: any;
  checkin: string;
  checkout: string;
  deviceType: string;
  searchAlternate: any;
  currentAirport?: IAirport;
  isNewLanding?: boolean;
  isCity?: boolean;
  testScope?: any;
}) {
  const { searchStore } = useMobxStores();
  const cardsRef = useRef<any>([]);
  const { t } = useTranslation();

  const hasHighlight = searchStore.filteredFacilities?.some(
    (x) => x.facility_branded == 1
  );

  const baseFacilities =
    searchStore.filteredFacilities ?? searchAlternate?.result;


  const showLowestPriceMessage = baseFacilities?.some(
    (item) => item.facility_disable_lowest_price === 0
  );
  useEffect(() => {
    if (!isServer) {
      setStorageCheckinCheckout(checkin, checkout);
    }
  }, [checkin, checkout]);
  return (
    // <Skeleton
    //   active
    //   loading={searchStore.isLoading}
    //   className="max-w-2xl mx-auto"
    // >
    <>
      {baseFacilities?.map((item, index) => {
        return (
          <div key={index} className="mb-4 mt-2 px-2">
            <Card
              configInfo={configInfo}
              item={item}
              index={index}
              cardsRef={cardsRef}
              checkin={checkin}
              checkout={checkout}
              hasHighlight={hasHighlight}
              deviceType={deviceType}
              isCity={isCity}
            />
          </div>
        );
      })}
      {!isNewLanding && showLowestPriceMessage && (
        <p className="text-[0.625rem] ml-2 mt-1.5 mb-4 xl:mb-2 xl:text-sm">
          {t('price_match_guarantee')}
        </p>
      )}

      {searchStore.filteredFacilities?.length === 0 && (
        <NeedMoreData airport={currentAirport} />
      )}
    </>
  );
}

export default observer(SearchList);
